/**
 * Global ordering-flow store (zustand).
 *
 * Owns: project {id, token}, live pricing, photos[], prints[] (each print may
 * carry an optional frame_id per the FRAMING MODEL), the selection set, the
 * apply bar, the current step, and persisted UI bits.
 *
 * Side-effects it manages:
 *  - localStorage persistence of the project handle (`rpl_portal_project`) and
 *    the first-time-guide flag (`rpl_guide_seen`).
 *  - Bootstrap/resume: ?resume=<id>.<token> → GET, else localStorage → GET,
 *    else POST /projects. A 404 on resume marks the project `expired`.
 *  - Decode + upload orchestration (bounded-concurrency pools).
 *  - Debounced (2s) PATCH autosave of prints + ui_state.
 *
 * The upload queue and save timer live OUTSIDE reactive state (module refs) so
 * they never trigger re-renders.
 */

import {
  borderModesFor,
  buildCatalog,
  computeTotals,
  ctypeSizeId,
  fineArtSizeId,
  framedProduct,
  parseSizeId,
  seedPricing,
  sizeStringToInches,
  type BorderMode,
  type PaperId,
  type PricingData,
  type PrintSelection,
  type Totals,
} from '@rpl/pricing';
import { useMemo } from 'react';
import { create } from 'zustand';
import { track } from '../lib/analytics';
import { api } from '../lib/api';
import { ApiError } from '../lib/api';
import { customResolvedSize, isCustomSizeId } from '../lib/customSize';
import { decodeFile, type DecodeLimits } from '../lib/decode';
import { decodeMegapixelCap } from '../lib/decode/megapixelCap';
import { checkPrint, DEFAULT_THRESHOLDS, thresholdsFromPricing } from '../lib/printCheck';
import { asRotation, rotatedDims } from '../lib/rotate';
import type { CfgStep } from '../components/sizepaper/cfgSteps';
import { isValidShareParam, shouldConfirmShareAccept } from '../lib/share';
import {
  effectiveBorderFor,
  isCtPaper,
  isFlaggedPrint,
  paperForSize,
  pricedPrints,
  priceForSafe,
} from '../lib/sizePaper';
import { planFileAcceptance, splitDuplicateFiles } from '../lib/uploadLimits';
import type {
  LocalPhoto,
  PhotoUiStatus,
  PrintUpsert,
  ProjectState,
  SortMode,
  StagedAdjust,
  UiPrint,
  UiStatePersist,
} from '../lib/types';
import { UploadQueue, type UploadItem } from '../lib/upload';
import { setUpsellProjectProvider } from '../lib/upsellReport';
import { reportUploadError } from '../lib/uploadErrorReport';
import { uid } from '../lib/util';

const PROJECT_KEY = 'rpl_portal_project';
const GUIDE_KEY = 'rpl_guide_seen';
const AUTOSAVE_MS = 2000;
/** A save waits at most this long for the one before it (see flushSave). */
const SAVE_QUEUE_WAIT_MS = 10_000;
const DECODE_CONCURRENCY = 4;
/** Session recents cap for reusable custom sizes (dropdown list length). */
const CUSTOM_SIZE_RECENTS = 8;

export type ProjectPhase = 'idle' | 'loading' | 'ready' | 'expired' | 'error';

/** Share-accept boot flow (?share=<id>.<token>). */
export type ShareFlow =
  | { state: 'confirm'; param: string } // device has an order with prints — ask first
  | { state: 'accepting'; param: string }
  | { state: 'failed'; param: string; error: 'share_expired' | 'share_not_found' | 'network' };

export interface StoreState {
  // ---- project ----
  project: { id: string; token: string } | null;
  phase: ProjectPhase;

  // ---- share accept flow ----
  shareFlow: ShareFlow | null;
  /** Photo count for the "N photos were shared with you" banner on Step 2. */
  shareBanner: number | null;

  // ---- pricing ----
  pricing: PricingData | null;
  pricingVersion: number | null;

  // ---- data ----
  photos: LocalPhoto[];
  prints: UiPrint[];
  selection: Set<string>;
  /** Session-recent custom sizes (cu- ids, newest first) for dropdown reuse. */
  customSizes: string[];

  // ---- ui ----
  applyBar: { sizeId: string | null; paper: PaperId; qty: number };
  step: number;
  sort: SortMode;
  /** Step-2 (Size & Paper) grid sort — separate from the upload screen's `sort`
   * so its 'todo' default (photos-without-prints first) never leaks to Upload. */
  sortSp: SortMode;
  guideSeen: boolean;

  // ---- lifecycle ----
  bootstrap: (resumeParam?: string | null, shareParam?: string | null) => Promise<void>;
  restart: () => Promise<void>;

  // ---- share actions ----
  /** Accept a share into a NEW project (replaces the device's stored order). */
  acceptShare: (param: string) => Promise<void>;
  /** Cancel in the replace-confirm dialog: keep the current order. */
  dismissShareConfirm: () => void;
  dismissShareBanner: () => void;
  /** Leave a failed-share screen and boot normally (stored order or fresh). */
  exitShare: () => Promise<void>;

  // ---- photos ----
  addFiles: (files: File[]) => Promise<void>;
  removePhoto: (localId: string) => void;
  retryPhoto: (localId: string) => void;

  // ---- selection ----
  toggleSelect: (localId: string) => void;
  setSelection: (ids: Iterable<string>) => void;
  selectAll: () => void;
  clearSelection: () => void;

  // ---- prints (extension surface for the Size & Paper / Cart agents) ----
  replacePrints: (prints: UiPrint[]) => void;
  addPrints: (prints: UiPrint[]) => void;
  updatePrint: (id: string, patch: Partial<UiPrint>) => void;
  removePrint: (id: string) => void;
  removePrintsForPhoto: (photoId: string) => void;
  /** Set one print's border mode (marks fine-art prints as border-decided). */
  setBorderMode: (id: string, mode: BorderMode) => void;
  /** Apply a border mode across loose prints (selected photos or all). */
  bulkSetBorder: (mode: BorderMode, scope: 'selected' | 'all') => void;
  /**
   * FRAMING = CONVERT (not add): move `qty` units of a loose print into a framed
   * print (same photo/paper, frameId set, border 'none', price = framed coupled
   * price). The framed print carries the FRAME'S print size (any-size upsell,
   * owner 2026-07-25) — when that differs from the loose size the crop resets
   * on an aspect change (the centered auto-crop recomputes) and Print Check
   * reruns; a same-size convert inherits crop/fit/Print-Check untouched. The
   * loose line loses `qty` (removed at 0); merges into an existing matching
   * framed line. No-op if the print is already framed or `qty` ≤ 0.
   */
  /** @param allowLowRes carry the customer's "frame it anyway" consent onto
   *  the converted print. Without it a frame big enough to push the photo
   *  under the DPI floor produces a BLOCKED print, which is unpriced and
   *  therefore invisible in the cart — the line looks deleted (owner-
   *  reported 2026-07-27). AddFrameDialog now asks before that can happen. */
  convertToFramed: (
    printId: string,
    qty: number,
    frameId: string,
    allowLowRes?: boolean,
  ) => string | null;
  /** Reverse of convertToFramed: turn a framed print back into a loose print. */
  unframe: (printId: string) => void;
  /** Remember a created custom size for session reuse (newest first, deduped). */
  addCustomSize: (sizeId: string) => void;

  // ---- ui setters ----
  setApplyBar: (patch: Partial<StoreState['applyBar']>) => void;
  setStep: (step: number) => void;
  setSort: (sort: SortMode) => void;
  setSortSp: (sort: SortMode) => void;
  /** Paper Details dialog — store-held so it SURVIVES desktop<->mobile
   * layout remounts (Win+Arrow snapping used to close it). */
  paperInfoOpen: boolean;
  setPaperInfoOpen: (open: boolean) => void;
  /** Size & Paper mobile tab + cart slide-over — store-held so the browser
   * back-gesture sync (lib/backNav) can drive them, and they survive layout
   * remounts (same reasoning as paperInfoOpen). UI-only, never persisted. */
  spTab: 'photos' | 'configure' | 'cart';
  setSpTab: (tab: 'photos' | 'configure' | 'cart') => void;
  spCartOpen: boolean;
  setSpCartOpen: (open: boolean) => void;
  /** The Customise pane's picked frame — store-held so a mobile tab switch
   * (which unmounts the pane) can't silently drop it: losing it turned "Add
   * framed" back into a loose add that bumped the wrong cart line. UI-only. */
  spPendingFrameId: string | null;
  setSpPendingFrameId: (frameId: string | null) => void;
  /** Frame COLOUR the choosers are showing (owner 2026-07-28). Store-held for
   * the same reason as spPendingFrameId: MobileTabs unmounts the pane, and a
   * lost colour would silently hand back the default. UI-only, never persisted
   * — the chosen frame's own id already carries the colour into the order. */
  spFrameColour: string;
  setSpFrameColour: (colour: string) => void;
  /** The CART's crop editor (the frame upsell's low-res handoff opens it on
   * the print it just created). Store-held for the same reason as
   * spPendingFrameId: the conversion removes the loose group, so the card that
   * owned the dialog unmounts — and anything that remounts the cart body would
   * take a useState with it, which is exactly how this silently did nothing
   * (owner 2026-07-27). UI-only, never persisted. */
  cartCropId: string | null;
  setCartCropId: (printId: string | null) => void;
  /** Post-Add frame nudge shown this order (upsell #1) — ONE ask per order,
   * and wizard orders never get it. UI-only, never persisted. */
  frameNudgeSeen: boolean;
  /** True once the customer PICKED a size (or a wizard applied one) — the
   *  anchor default must never override a human choice. UI-only. */
  sizeTouched: boolean;
  /** The anchor default that was auto-applied (null = not yet). Doubles as
   *  the once-per-order guard and the accepted-beacon reference. UI-only. */
  anchorSizeId: string | null;
  /** Cart-exit small-prints sheet shown (once per order). UI-only. */
  smallPrintsSeen: boolean;
  /** Order was built by the wizard — bulk orders never get cart upsells. */
  wizardApplied: boolean;
  setFrameNudgeSeen: (seen: boolean) => void;
  /** Apply the anchor default WITHOUT flipping sizeTouched (setApplyBar does). */
  applyAnchorSize: (sizeId: string, paper: PaperId) => void;
  setSmallPrintsSeen: (seen: boolean) => void;
  setWizardApplied: () => void;
  /** Mobile stepped Customise position (owner 2026-07-27). Store-held for the
   * SAME reason as spPendingFrameId: MobileTabs unmounts the inactive panes,
   * so local step state would snap back to 'size' on every tab peek. A string
   * union (not an index) because the border step is conditional — a stale key
   * just clamps to the last step. UI-only, never persisted. */
  spCfgStep: CfgStep;
  setSpCfgStep: (step: CfgStep) => void;
  /** The Customise pane's border choice. Store-held for the same unmount
   * reason: losing a fine-art border mid-flow would silently create undecided
   * prints and re-open the border gate. null = fine art not yet chosen. */
  spBorder: BorderMode | null;
  setSpBorder: (mode: BorderMode | null) => void;
  /** Desktop Photos-pane width override (owner 2026-07-27 adaptive tiers):
   * 'auto' follows the photo count (rail ≤3 · narrow ≤12 · full); a manual
   * rail/full choice is STICKY for the session so we never fight the
   * customer. UI-only. */
  spPhotosMode: 'auto' | 'rail' | 'full';
  setSpPhotosMode: (mode: 'auto' | 'rail' | 'full') => void;
  /** Pre-Add adjustments STAGED from the Customise ✎ dialog, keyed by photo
   * (owner 2026-07-27: Save there must NOT add to the order — the staged
   * crop/rotation/B&W apply when Add creates the prints). UI-only. */
  stagedAdjust: Record<string, StagedAdjust>;
  setStagedAdjust: (photoId: string, adj: StagedAdjust | null) => void;
  clearStagedAdjust: (photoIds: Iterable<string>) => void;
  /** Customer instructions for the lab — persisted in ui_state, sent as the
   * Shopify cart note at handoff. */
  orderNote: string;
  setOrderNote: (note: string) => void;
  /** Urgent-processing upsell — persisted in ui_state, sent in the checkout body. */
  urgent: boolean;
  setUrgent: (urgent: boolean) => void;
  markGuideSeen: () => void;

  /**
   * Save NOW instead of waiting out the 2 s autosave debounce: cancels the
   * pending timer, waits for any save already in flight, then PATCHes the
   * current state. Resolves true when that PATCH succeeded. POST /checkout
   * builds the Shopify cart from the SERVER's prints, so checkout must await
   * this first or the last 2 s of changes never reach the cart.
   */
  flushNow: () => Promise<boolean>;

  // ---- internal ----
  _patchPhoto: (localId: string, patch: Partial<LocalPhoto>) => void;
}

/* ---------- module-level, non-reactive refs ---------- */
let uploadQueue: UploadQueue | null = null;
let uploadQueueKey = '';
let saveTimer: ReturnType<typeof setTimeout> | null = null;
/** Saves run one at a time (see flushSave). */
let saveChain: Promise<unknown> = Promise.resolve();
/** Why the latest save failed (null after a good save), for checkout's error. */
let lastSaveError: { code: string; status: number } | null = null;
export function lastSaveFailure(): { code: string; status: number } | null {
  return lastSaveError;
}
let hydrating = false;

/** Resolves when `p` settles or after `ms`, whichever comes first. */
function settledOrAfter(p: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const t = setTimeout(resolve, ms);
    const done = () => {
      clearTimeout(t);
      resolve();
    };
    p.then(done, done);
  });
}

function readStoredProject(): { id: string; token: string } | null {
  try {
    const raw = localStorage.getItem(PROJECT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: string; token?: string };
    if (parsed.id && parsed.token) return { id: parsed.id, token: parsed.token };
  } catch {
    /* ignore */
  }
  return null;
}

function persistProject(project: { id: string; token: string } | null): void {
  try {
    if (project) localStorage.setItem(PROJECT_KEY, JSON.stringify(project));
    else localStorage.removeItem(PROJECT_KEY);
  } catch {
    /* storage disabled — non-fatal */
  }
}

function readGuideSeen(): boolean {
  try {
    return localStorage.getItem(GUIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Map a wire upload_status → the grid's UI status. */
function phaseForUpload(status: string): PhotoUiStatus {
  if (status === 'uploaded') return 'ready';
  if (status === 'failed') return 'error';
  return 'error'; // 'pending' with no local file can't resume
}

function hydratePhotos(proj: ProjectState, projectId: string, token: string): LocalPhoto[] {
  return proj.photos
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => {
      const status = phaseForUpload(p.upload_status);
      return {
        id: p.photo_id, // hydrated photos key by their server id
        photoId: p.photo_id,
        filename: p.filename,
        file: null,
        previewUrl: p.upload_status === 'uploaded' ? api.previewUrl(projectId, p.photo_id, token) : null,
        width: p.width,
        height: p.height,
        mp: (p.width * p.height) / 1_000_000,
        bytes: p.bytes,
        sha256: p.sha256,
        status,
        progress: status === 'ready' ? 100 : 0,
        ...(status === 'error' ? { error: "This upload didn't finish. Remove it and add it again." } : {}),
        sortOrder: p.sort_order,
      };
    });
}

function hydratePrints(proj: ProjectState, pricing: PricingData): UiPrint[] {
  return proj.prints.map((p) => ({
    id: p.print_id,
    printId: p.print_id,
    photoId: p.photo_id,
    sizeId: p.size_id,
    // Normalise papers that are no longer offered for the size (e.g. an order
    // saved with 8x10 Gloss before the lustre-only restriction) — otherwise
    // the server rejects the next autosave for the whole print set.
    paper: paperForSize(pricing, p.size_id, p.paper),
    qty: p.qty,
    fitMode: p.fit_mode,
    cropRect: p.crop_rect,
    printCheck: p.print_check,
    overrideLowRes: p.override_lowres,
    effectiveDpi: p.effective_dpi,
    unitPrice: p.unit_price,
    borderMode: p.border_mode ?? 'none',
    borderMm: p.border_mm ?? null,
    bwStyle: p.bw_style ?? null,
    bwStrength: p.bw_strength ?? null,
    rotation: asRotation(p.rotation),
    bw: !!p.bw,
    // A hydrated (previously saved) print already made its border choice — don't
    // re-gate on resume.
    borderDecided: true,
    ...(p.frame_id ? { frameId: p.frame_id } : {}),
  }));
}

/** Distinct cu- sizeIds in a print set (session recents seed on resume). */
function customSizesIn(prints: readonly UiPrint[]): string[] {
  const out: string[] = [];
  for (const p of prints) {
    if (isCustomSizeId(p.sizeId) && !out.includes(p.sizeId)) out.push(p.sizeId);
  }
  return out;
}

export const useStore = create<StoreState>((set, get) => {
  /* ---- upload plumbing ---- */
  function ensureUploadQueue(): UploadQueue | null {
    const { project } = get();
    if (!project) return null;
    const key = `${project.id}:${project.token}`;
    if (uploadQueue && uploadQueueKey === key) return uploadQueue;
    uploadQueueKey = key;
    uploadQueue = new UploadQueue(
      { projectId: project.id, token: project.token, api },
      {
        onStatus: (id, status) => get()._patchPhoto(id, { status }),
        onProgress: (id, progress) => get()._patchPhoto(id, { progress }),
        onPhotoId: (id, photoId) => get()._patchPhoto(id, { photoId }),
        onError: (id, message) => get()._patchPhoto(id, { status: 'error', error: message }),
        onDone: (id, photoId) =>
          get()._patchPhoto(id, { status: 'ready', progress: 100, photoId }),
      },
    );
    return uploadQueue;
  }

  /* ---- autosave ---- */
  function scheduleSave(): void {
    if (hydrating) return;
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      saveTimer = null;
      void flushSave();
    }, AUTOSAVE_MS);
  }

  /** Serialised: each save waits for the previous one and reads the store
   *  when it STARTS, so the last PATCH to land is always the newest state (an
   *  older autosave still in flight can never overwrite a checkout flush).
   *  The wait is capped, so one request lost on a dropped connection can
   *  never block every later save (and checkout) behind it. */
  function flushSave(): Promise<boolean> {
    const run = settledOrAfter(saveChain, SAVE_QUEUE_WAIT_MS)
      .then(saveNow)
      .catch(() => false);
    saveChain = run;
    return run;
  }

  /** One PATCH of prints + ui_state. True when the server accepted it. */
  async function saveNow(): Promise<boolean> {
    const { project, prints, photos, step, applyBar, sort, guideSeen } = get();
    if (!project) return false;
    // Resolve client photoIds → server photo_ids.
    const serverId = new Map<string, string>();
    for (const ph of photos) if (ph.photoId) serverId.set(ph.id, ph.photoId);
    // PATCH prints is replace-set: only send it when EVERY print resolves to an
    // uploaded photo, otherwise a still-uploading print would be dropped. If a
    // print references an in-flight photo, defer the prints save until it lands.
    const allResolved = prints.every((pr) => serverId.has(pr.photoId));
    const upserts: PrintUpsert[] = [];
    for (const pr of prints) {
      const photo_id = serverId.get(pr.photoId);
      if (!photo_id) continue;
      upserts.push({
        ...(pr.printId ? { print_id: pr.printId } : {}),
        photo_id,
        size_id: pr.sizeId,
        paper: pr.paper,
        qty: pr.qty,
        fit_mode: pr.fitMode,
        crop_rect: pr.cropRect,
        print_check: pr.printCheck,
        override_lowres: pr.overrideLowRes,
        ...(pr.effectiveDpi != null ? { effective_dpi: pr.effectiveDpi } : {}),
        ...(pr.frameId ? { frame_id: pr.frameId } : {}),
        // Clamped to what the size offers: the server rejects the WHOLE
        // replace-set PATCH for one bad border (e.g. handling on a C-Type
        // size), which would silently stop every later autosave.
        border_mode: pr.frameId ? 'none' : effectiveBorderFor(pr.sizeId, pr.borderMode),
        ...(pr.borderMm != null && !pr.frameId && (pr.borderMode ?? 'none') === 'white'
          ? { border_mm: pr.borderMm }
          : {}),
        rotation: pr.rotation ?? 0,
        bw: pr.bw ?? false,
        ...(pr.bw && pr.bwStyle ? { bw_style: pr.bwStyle } : {}),
        ...(pr.bw && (pr.bwStyle === 'v2' || pr.bwStyle === 'v3')
          ? { bw_strength: pr.bwStrength ?? 100 }
          : {}),
      });
    }
    const ui_state: UiStatePersist = {
      step,
      applyBar: { sizeId: applyBar.sizeId, paper: applyBar.paper, qty: applyBar.qty },
      sort,
      guideSeen,
      orderNote: get().orderNote,
      urgent: get().urgent,
    };
    const body = allResolved ? { prints: upserts, ui_state } : { ui_state };
    let ok = true;
    try {
      await api.patchProject(project.id, project.token, body);
      lastSaveError = null;
    } catch (err) {
      ok = false;
      lastSaveError =
        err instanceof ApiError
          ? { code: err.code, status: err.httpStatus }
          : { code: 'network', status: 0 };
      if (err instanceof ApiError && err.httpStatus === 404) {
        persistProject(null);
        set({ phase: 'expired' });
      }
      // other errors are transient — the next change reschedules a save
    }
    // If prints were deferred, retry once the in-flight uploads settle.
    if (!allResolved && get().photos.some((p) => p.status === 'uploading' || p.status === 'hashing')) {
      scheduleSave();
    }
    return ok;
  }

  /* ---- decode pool ---- */
  async function decodeAndQueue(localId: string, file: File): Promise<void> {
    const { pricing } = get();
    const limits: DecodeLimits | undefined = pricing
      ? {
          maxBytes: pricing.limits.maxFileBytes,
          maxMegapixels: decodeMegapixelCap(pricing.limits),
        }
      : undefined;
    try {
      const result = await decodeFile(file, limits);
      // photo may have been removed mid-decode
      if (!get().photos.some((p) => p.id === localId)) return;
      const previewUrl = URL.createObjectURL(result.previewBlob);
      get()._patchPhoto(localId, {
        width: result.width,
        height: result.height,
        mp: result.mp,
        previewUrl,
        status: 'uploading',
        progress: 0,
      });
      const item: UploadItem = {
        localId,
        file,
        filename: file.name,
        bytes: file.size,
        width: result.width,
        height: result.height,
        previewBlob: result.previewBlob,
      };
      ensureUploadQueue()?.enqueue(item);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "We couldn't read this file.";
      get()._patchPhoto(localId, { status: 'error', error: message });
      reportUploadError(get().project, file, err);
    }
  }

  /** Hydrate a fetched project into state + persist the handle. */
  function applyProject(
    handle: { id: string; token: string },
    proj: ProjectState,
    extra: Partial<StoreState> = {},
  ): void {
    hydrating = true;
    const ui = (proj.ui_state ?? {}) as UiStatePersist;
    const prints = hydratePrints(proj, get().pricing ?? seedPricing);
    set({
      project: handle,
      phase: 'ready',
      photos: hydratePhotos(proj, handle.id, handle.token),
      prints,
      selection: new Set<string>(),
      customSizes: customSizesIn(prints),
      step: typeof ui.step === 'number' ? ui.step : 0,
      orderNote: typeof ui.orderNote === 'string' ? ui.orderNote : '',
      urgent: typeof ui.urgent === 'boolean' ? ui.urgent : false,
      sort: ui.sort ?? 'upload',
      guideSeen: readGuideSeen() || !!ui.guideSeen,
      applyBar: ui.applyBar
        ? { sizeId: ui.applyBar.sizeId, paper: ui.applyBar.paper, qty: ui.applyBar.qty }
        : get().applyBar,
      // A resumed order that stored a size already made its choice — the
      // anchor default must not fight it.
      sizeTouched: !!(ui.applyBar && ui.applyBar.sizeId),
      ...extra,
    });
    hydrating = false;
    persistProject(handle);
  }

  return {
    project: null,
    phase: 'idle',
    shareFlow: null,
    shareBanner: null,
    pricing: null,
    pricingVersion: null,
    photos: [],
    prints: [],
    selection: new Set<string>(),
    customSizes: [],
    applyBar: { sizeId: null, paper: 'gloss', qty: 1 },
    step: 0,
    sort: 'upload',
    sortSp: 'todo',
    paperInfoOpen: false,
    frameNudgeSeen: false,
    sizeTouched: false,
    anchorSizeId: null,
    smallPrintsSeen: false,
    wizardApplied: false,
    spTab: 'photos',
    spCartOpen: false,
    spPendingFrameId: null,
    spFrameColour: 'black',
    cartCropId: null,
    spCfgStep: 'size',
    spBorder: null,
    spPhotosMode: 'auto',
    stagedAdjust: {},
    orderNote: '',
    urgent: false,
    guideSeen: readGuideSeen(),

    async bootstrap(resumeParam, shareParam) {
      if (get().phase === 'loading') return;
      set({ phase: 'loading' });

      const pricingPromise = api.getPricing().catch(() => null);

      const pricing = await pricingPromise;
      if (pricing) set({ pricing: pricing.data, pricingVersion: pricing.version });
      else set({ pricing: seedPricing, pricingVersion: seedPricing.version }); // offline fallback

      // ?share=<id>.<token> takes precedence over ?resume= — someone followed a
      // friend's link; they want the shared photos, not an old session.
      if (shareParam) {
        if (!isValidShareParam(shareParam)) {
          set({
            shareFlow: { state: 'failed', param: shareParam, error: 'share_not_found' },
            phase: 'ready',
          });
          return;
        }
        const stored = readStoredProject();
        if (stored) {
          try {
            const proj = await api.getProject(stored.id, stored.token);
            if (shouldConfirmShareAccept(proj.prints.length)) {
              // The device has a real order in progress — hydrate it so Cancel /
              // "Save my order" work, and ask before replacing it.
              applyProject(stored, proj, {
                shareFlow: { state: 'confirm', param: shareParam },
              });
              return;
            }
          } catch {
            // stored project unusable (expired/network) — accept replaces it,
            // mirroring the resume path's fall-through behaviour.
          }
        }
        await get().acceptShare(shareParam);
        return;
      }

      // Resolve project handle: ?resume=<id>.<token> wins, else localStorage.
      let handle: { id: string; token: string } | null = null;
      if (resumeParam) {
        const dot = resumeParam.indexOf('.');
        if (dot > 0) {
          handle = { id: resumeParam.slice(0, dot), token: resumeParam.slice(dot + 1) };
        }
      }
      if (!handle) handle = readStoredProject();

      if (handle) {
        try {
          const proj = await api.getProject(handle.id, handle.token);
          // Once a project has been ORDERED, don't reopen it — the customer has
          // already checked out, so a return visit should start a fresh order
          // rather than re-showing the placed order's photos. Drafts / in-progress
          // (unpaid) checkouts still resume. The emailed resume/share links can
          // still reach an ordered project deliberately.
          if (proj.status === 'ordered') {
            persistProject(null);
            // fall through to create a fresh project below
          } else {
            applyProject(handle, proj);
            return;
          }
        } catch (err) {
          if (err instanceof ApiError && err.httpStatus === 404) {
            persistProject(null);
            set({ phase: 'expired', project: null });
            return;
          }
          // fall through and create a fresh project
        }
      }

      try {
        const created = await api.createProject();
        const project = { id: created.project_id, token: created.token };
        persistProject(project);
        set({ project, phase: 'ready' });
        track('portal_start');
      } catch {
        set({ phase: 'error' });
      }
    },

    async acceptShare(param) {
      set({ shareFlow: { state: 'accepting', param }, phase: 'loading' });
      try {
        const res = await api.acceptShare(param);
        const handle = { id: res.project_id, token: res.token };
        const proj = await api.getProject(handle.id, handle.token);
        // Fresh upload queue for the new project.
        uploadQueue = null;
        uploadQueueKey = '';
        applyProject(handle, proj, {
          shareFlow: null,
          shareBanner: res.photo_count,
          step: 1, // land on Size & Paper with the shared photos loaded
          selection: new Set<string>(),
        });
        scheduleSave(); // persist ui_state.step so a refresh returns to Step 2
        track('share_accepted', { photos: res.photo_count });
      } catch (err) {
        const error =
          err instanceof ApiError && (err.code === 'share_expired' || err.code === 'share_not_found')
            ? err.code
            : ('network' as const);
        set({ shareFlow: { state: 'failed', param, error }, phase: 'ready' });
      }
    },

    dismissShareConfirm() {
      set({ shareFlow: null });
    },

    dismissShareBanner() {
      set({ shareBanner: null });
    },

    async exitShare() {
      set({ shareFlow: null, shareBanner: null, phase: 'idle' });
      await get().bootstrap();
    },

    async restart() {
      persistProject(null);
      uploadQueue = null;
      uploadQueueKey = '';
      // revoke object URLs
      for (const p of get().photos) if (p.file && p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      set({
        project: null,
        phase: 'idle',
        shareFlow: null,
        shareBanner: null,
        photos: [],
        prints: [],
        selection: new Set<string>(),
        customSizes: [],
        step: 0,
        frameNudgeSeen: false,
    sizeTouched: false,
    anchorSizeId: null,
    smallPrintsSeen: false,
    wizardApplied: false,
      });
      await get().bootstrap();
    },

    async addFiles(offered) {
      const { project, pricing } = get();
      if (!project) return;
      const limits = pricing?.limits ?? seedPricing.limits;

      // Same duplicate split the upload screen messages about — one photo can
      // be printed on several sizes/papers in Size & Paper, so an identical
      // re-upload (filename + bytes) is always skipped.
      const { fresh: files } = splitDuplicateFiles(get().photos, offered);
      if (files.length === 0) return;

      // Create placeholder cards immediately (decoding state). Adds are gated
      // by TOTAL ORDER BYTES (5 GB customer cap) + the photo-count safety rail
      // — files that would cross either cap are skipped (the screen messages).
      const accepted: Array<{ localId: string; file: File }> = [];
      set((state) => {
        const plan = planFileAcceptance(
          {
            count: state.photos.length,
            bytes: state.photos.reduce((n, p) => n + p.bytes, 0),
          },
          files.map((f) => f.size),
          limits,
        );
        const next = state.photos.slice();
        for (const i of plan.acceptedIndexes) {
          const file = files[i]!;
          const localId = uid('ph');
          accepted.push({ localId, file });
          next.push({
            id: localId,
            photoId: null,
            filename: file.name,
            file,
            previewUrl: null,
            width: 0,
            height: 0,
            mp: 0,
            bytes: file.size,
            sha256: null,
            status: 'decoding',
            progress: 0,
            sortOrder: next.length,
          });
        }
        return { photos: next };
      });

      if (accepted.length > 0) track('upload_started', { files: accepted.length });

      // Bounded decode pool.
      let cursor = 0;
      const workers = new Array(Math.min(DECODE_CONCURRENCY, accepted.length))
        .fill(0)
        .map(async () => {
          while (cursor < accepted.length) {
            const item = accepted[cursor++]!;
            await decodeAndQueue(item.localId, item.file);
          }
        });
      await Promise.all(workers);
    },

    removePhoto(localId) {
      const { project, photos } = get();
      const photo = photos.find((p) => p.id === localId);
      uploadQueue?.cancel(localId);
      if (photo?.file && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
      if (project && photo?.photoId) {
        void api.deletePhoto(project.id, project.token, photo.photoId).catch(() => {});
      }
      set((state) => {
        const selection = new Set(state.selection);
        selection.delete(localId);
        return {
          photos: state.photos.filter((p) => p.id !== localId),
          prints: state.prints.filter((pr) => pr.photoId !== localId),
          selection,
        };
      });
      scheduleSave();
    },

    retryPhoto(localId) {
      const photo = get().photos.find((p) => p.id === localId);
      if (!photo?.file) return;
      get()._patchPhoto(localId, { status: 'decoding', error: undefined, progress: 0 });
      void decodeAndQueue(localId, photo.file);
    },

    toggleSelect(localId) {
      set((state) => {
        const selection = new Set(state.selection);
        if (selection.has(localId)) selection.delete(localId);
        else selection.add(localId);
        return { selection };
      });
    },
    setSelection(ids) {
      set({ selection: new Set(ids) });
    },
    selectAll() {
      set((state) => ({
        selection: new Set(state.photos.filter((p) => p.status === 'ready').map((p) => p.id)),
      }));
    },
    clearSelection() {
      set({ selection: new Set<string>() });
    },

    replacePrints(prints) {
      set({ prints });
      scheduleSave();
    },
    addPrints(prints) {
      set((state) => ({ prints: [...state.prints, ...prints] }));
      scheduleSave();
    },
    updatePrint(id, patch) {
      set((state) => ({
        prints: state.prints.map((p) => (p.id === id ? { ...p, ...patch } : p)),
      }));
      scheduleSave();
    },
    removePrint(id) {
      set((state) => ({ prints: state.prints.filter((p) => p.id !== id) }));
      scheduleSave();
    },
    removePrintsForPhoto(photoId) {
      set((state) => ({ prints: state.prints.filter((p) => p.photoId !== photoId) }));
      scheduleSave();
    },
    setBorderMode(id, mode) {
      set((state) => ({
        prints: state.prints.map((p) => {
          if (p.id !== id || p.frameId) return p;
          if (!borderModesFor(p.sizeId, false).includes(mode)) return p;
          return { ...p, borderMode: mode, borderDecided: true };
        }),
      }));
      scheduleSave();
    },
    bulkSetBorder(mode, scope) {
      set((state) => ({
        prints: state.prints.map((p) => {
          if (p.frameId) return p;
          if (scope === 'selected' && !state.selection.has(p.photoId)) return p;
          if (!borderModesFor(p.sizeId, false).includes(mode)) return p; // e.g. handling on C-Type
          return { ...p, borderMode: mode, borderDecided: true };
        }),
      }));
      scheduleSave();
    },

    convertToFramed(printId, qty, frameId, allowLowRes = false) {
      const { pricing } = get();
      /* The id of the line the print ended up on — a NEW framed row, or the
         existing one it merged into. Returned so a caller can act on it (the
         cart's low-res upsell opens the crop editor on it) without having to
         re-derive it from the store with a predicate, which is fragile and
         silently produced "nothing happens" on the first attempt. */
      let landedId: string | null = null;
      set((state) => {
        const loose = state.prints.find((p) => p.id === printId);
        if (!loose || loose.frameId) return {};
        const moveQty = Math.min(qty, loose.qty);
        if (moveQty <= 0) return {};
        let framedPrice: number | null = loose.unitPrice;
        // ANY-SIZE upsell (owner 2026-07-25): the frame's print size WINS — the
        // server validates frame_id against size/paper, so the framed row must
        // carry the frame's own size/paper.
        let targetSizeId = loose.sizeId;
        let targetPaper = loose.paper;
        try {
          if (pricing) {
            const fp = framedProduct(pricing, frameId);
            framedPrice = fp.price;
            targetSizeId = (isCtPaper(pricing, fp.paper) ? ctypeSizeId : fineArtSizeId)(fp.printSize);
            targetPaper = fp.paper;
          }
        } catch {
          return {}; // frameId not valid — leave the order untouched
        }
        // Crossing to a different print size: reset the crop when the aspect
        // changes (the centered auto-crop recomputes for the new shape) and
        // rerun Print Check for the new size. A same-size convert inherits
        // crop/fit/Print-Check untouched, as before.
        let cropRect = loose.cropRect;
        let printCheck = loose.printCheck;
        let effectiveDpi = loose.effectiveDpi;
        let overrideLowRes = loose.overrideLowRes;
        if (pricing && targetSizeId !== loose.sizeId) {
          // "Print anyway" consent was given at the OLD size; the frame's size
          // has a different DPI story, so drop it — unless the customer just
          // gave fresh consent for THIS frame in the upsell dialog.
          overrideLowRes = allowLowRes;
          const catalog = buildCatalog(pricing);
          const oldInches =
            catalog.get(loose.sizeId)?.inches ??
            (isCustomSizeId(loose.sizeId) ? customResolvedSize(loose.sizeId).inches : null);
          const newInches =
            catalog.get(targetSizeId)?.inches ?? sizeStringToInches(parseSizeId(targetSizeId).size);
          const ratio = (i: readonly [number, number]) => Math.max(i[0], i[1]) / Math.min(i[0], i[1]);
          if (!oldInches || Math.abs(ratio(oldInches) - ratio(newInches)) > 1e-6) cropRect = null;
          const photo = state.photos.find((p) => p.id === loose.photoId);
          if (photo && photo.width > 0 && photo.height > 0) {
            const [rw, rh] = rotatedDims(photo.width, photo.height, asRotation(loose.rotation ?? 0));
            const thresholds = pricing.printCheck
              ? thresholdsFromPricing(pricing.printCheck)
              : DEFAULT_THRESHOLDS;
            const result = checkPrint(
              { width: rw, height: rh },
              newInches,
              loose.fitMode === 'crop' ? cropRect : null,
              loose.fitMode,
              parseSizeId(targetSizeId).family === 'fine_art' ? 'fine_art' : 'ctype',
              thresholds,
            );
            printCheck = result.status;
            effectiveDpi = result.dpi;
          }
        }
        // An existing framed line for the same photo/size/paper/frame/B&W to merge into.
        const merge = state.prints.find(
          (p) =>
            p.id !== printId &&
            p.frameId === frameId &&
            p.photoId === loose.photoId &&
            p.sizeId === targetSizeId &&
            p.paper === targetPaper &&
            (p.bw ?? false) === (loose.bw ?? false),
        );
        const next: UiPrint[] = [];
        for (const p of state.prints) {
          if (p.id === printId) {
            const remaining = p.qty - moveQty;
            if (remaining > 0) next.push({ ...p, qty: remaining }); // else drop the loose line
            continue;
          }
          if (merge && p.id === merge.id) {
            // Consent belongs to the LINE, so a merge must not silently drop
            // the "frame it anyway" the customer just gave (the merged line is
            // otherwise blocked, and a blocked line is invisible in the cart).
            // The undo snapshot only survives if both sides agree on what they
            // came from; otherwise the line has no single "before" and Remove
            // frame falls back to a plain unframe.
            const agree =
              !!p.framedFrom &&
              p.framedFrom.sizeId === loose.sizeId &&
              p.framedFrom.paper === loose.paper &&
              p.framedFrom.borderMode === (loose.borderMode ?? 'none');
            landedId = p.id;
            next.push({
              ...p,
              qty: p.qty + moveQty,
              overrideLowRes: p.overrideLowRes || overrideLowRes,
              framedFrom: agree ? p.framedFrom : null,
            });
            continue;
          }
          next.push(p);
        }
        if (!merge) {
          const id = uid('pr');
          landedId = id;
          next.push({
            id,
            printId: null,
            photoId: loose.photoId,
            sizeId: targetSizeId,
            paper: targetPaper,
            qty: moveQty,
            fitMode: loose.fitMode,
            cropRect,
            printCheck,
            overrideLowRes,
            effectiveDpi,
            unitPrice: framedPrice,
            frameId,
            borderMode: 'none',
            borderDecided: true,
            rotation: loose.rotation,
            bw: loose.bw,
            // So "Remove frame" can put the print back exactly as it was.
            framedFrom: {
              sizeId: loose.sizeId,
              paper: loose.paper,
              fitMode: loose.fitMode,
              cropRect: loose.cropRect,
              printCheck: loose.printCheck,
              effectiveDpi: loose.effectiveDpi,
              overrideLowRes: loose.overrideLowRes ?? false,
              borderMode: loose.borderMode ?? 'none',
              borderDecided: loose.borderDecided ?? false,
            },
          });
        }
        return { prints: next };
      });
      scheduleSave();
      return landedId;
    },

    addCustomSize(sizeId) {
      set((state) => ({
        customSizes: [sizeId, ...state.customSizes.filter((s) => s !== sizeId)].slice(
          0,
          CUSTOM_SIZE_RECENTS,
        ),
      }));
    },

    unframe(printId) {
      const { pricing } = get();
      set((state) => {
        const framed = state.prints.find((p) => p.id === printId);
        if (!framed || !framed.frameId) return {};
        /* Revert to what the print WAS before framing when we recorded it
           (owner 2026-07-27). Without the snapshot the print keeps the FRAME'S
           print size, which for an upsell to a bigger frame is exactly the
           size the photo could not carry — so removing the frame left a
           blocked, invisible line. No snapshot (framed from scratch, an
           ambiguous merge, or a resumed project) falls back to the old
           behaviour: loose at the frame's own size. */
        const back = framed.framedFrom ?? null;
        const sizeId = back?.sizeId ?? framed.sizeId;
        const paper = back?.paper ?? framed.paper;
        const borderMode = back?.borderMode ?? 'none';
        const loosePrice = pricing ? priceForSafe(pricing, sizeId, paper) : framed.unitPrice;
        // Merge back into an existing loose line for the same photo/size/paper
        // — border joins the merge key.
        const merge = state.prints.find(
          (p) =>
            p.id !== printId &&
            !p.frameId &&
            p.photoId === framed.photoId &&
            p.sizeId === sizeId &&
            p.paper === paper &&
            (p.borderMode ?? 'none') === borderMode,
        );
        const next: UiPrint[] = [];
        for (const p of state.prints) {
          if (p.id === printId) {
            if (merge) continue; // folded into the loose line below
            next.push({
              ...p,
              frameId: undefined,
              unitPrice: loosePrice,
              sizeId,
              paper,
              borderMode,
              fitMode: back?.fitMode ?? p.fitMode,
              cropRect: back?.cropRect ?? p.cropRect,
              printCheck: back?.printCheck ?? p.printCheck,
              effectiveDpi: back?.effectiveDpi ?? p.effectiveDpi,
              // The consent given at the OLD size is valid again at that size.
              overrideLowRes: back ? back.overrideLowRes : p.overrideLowRes,
              // C-Type never gates; a newly-loose fine-art print must re-decide
              // its border unless the snapshot says it already had.
              borderDecided: back ? back.borderDecided : sizeId.startsWith('ct-'),
              framedFrom: null, // consumed
            });
            continue;
          }
          if (merge && p.id === merge.id) {
            next.push({ ...p, qty: p.qty + framed.qty });
            continue;
          }
          next.push(p);
        }
        return { prints: next };
      });
      scheduleSave();
    },

    setApplyBar(patch) {
      set((state) => ({
        applyBar: { ...state.applyBar, ...patch },
        // A size in the patch means a HUMAN picked it (the anchor default
        // goes through applyAnchorSize instead) — lock the anchor out.
        ...(patch.sizeId !== undefined ? { sizeTouched: true } : {}),
        // A fine-art-only border (handling) can't survive a move to a C-Type
        // size: clamp it in the SAME update, so no render ever pairs the new
        // size with the old border (ConfigurePane's reset effect runs a frame
        // late, and PrintPreview threw "Handling border is fine-art only").
        ...(patch.sizeId && state.spBorder != null &&
        effectiveBorderFor(patch.sizeId, state.spBorder) !== state.spBorder
          ? { spBorder: 'none' as const }
          : {}),
      }));
      scheduleSave();
    },
    setStep(step) {
      if (step !== get().step) track('step_view', { step });
      // Expose the step for the global error reporter (lib/errors.ts) to tag
      // uncaught errors with where the user was — decoupled from the store.
      try {
        (window as unknown as { __rplStep?: number }).__rplStep = step;
      } catch {
        /* SSR/tests: no window */
      }
      set({ step });
      scheduleSave();
    },
    setSort(sort) {
      set({ sort });
      scheduleSave();
    },
    setPaperInfoOpen(open) {
      set({ paperInfoOpen: open }); // ui-only, never persisted
    },
    setSpTab(tab) {
      set({ spTab: tab }); // ui-only, never persisted
    },
    setSpCartOpen(open) {
      set({ spCartOpen: open }); // ui-only, never persisted
    },
    setSpPendingFrameId(frameId) {
      set({ spPendingFrameId: frameId }); // ui-only, never persisted
    },
    setSpFrameColour(colour) {
      set({ spFrameColour: colour }); // ui-only, never persisted
    },
    setCartCropId(printId) {
      set({ cartCropId: printId }); // ui-only, never persisted
    },
    setFrameNudgeSeen(seen) {
      set({ frameNudgeSeen: seen });
    },
    applyAnchorSize(sizeId, paper) {
      // ui-only merchandising default — never counts as a customer choice
      set((state) => ({ applyBar: { ...state.applyBar, sizeId, paper }, anchorSizeId: sizeId }));
      scheduleSave();
    },
    setSmallPrintsSeen(seen) {
      set({ smallPrintsSeen: seen });
    },
    setWizardApplied() {
      set({ wizardApplied: true, sizeTouched: true });
    },
    setSpCfgStep(step) {
      set({ spCfgStep: step }); // ui-only, never persisted
    },
    setSpBorder(mode) {
      set({ spBorder: mode }); // ui-only, never persisted
    },
    setSpPhotosMode(mode) {
      set({ spPhotosMode: mode }); // ui-only, never persisted
    },
    setStagedAdjust(photoId, adj) {
      set((s) => {
        const next = { ...s.stagedAdjust };
        if (adj) next[photoId] = adj;
        else delete next[photoId];
        return { stagedAdjust: next };
      });
    },
    clearStagedAdjust(photoIds) {
      set((s) => {
        const next = { ...s.stagedAdjust };
        for (const id of photoIds) delete next[id];
        return { stagedAdjust: next };
      });
    },
    setOrderNote(note) {
      set({ orderNote: note });
      scheduleSave();
    },
    setUrgent(urgent) {
      set({ urgent });
      scheduleSave();
    },
    setSortSp(sort) {
      set({ sortSp: sort }); // Step-2 grid sort only — not persisted, not shared with Upload
    },
    markGuideSeen() {
      try {
        localStorage.setItem(GUIDE_KEY, '1');
      } catch {
        /* ignore */
      }
      set({ guideSeen: true });
      scheduleSave();
    },

    flushNow() {
      if (saveTimer) {
        clearTimeout(saveTimer);
        saveTimer = null;
      }
      return flushSave();
    },

    _patchPhoto(localId, patch) {
      set((state) => ({
        photos: state.photos.map((p) => (p.id === localId ? { ...p, ...patch } : p)),
      }));
    },
  };
});

/* AUDIT B2 (2026-08-26) — arm the upsell beacon HERE, not in a screen.
   SizePaperScreen used to register this provider in an effect whose cleanup
   nulled it, and the ordering flow is a mutually-exclusive switch: stepping to
   the Cart unmounted the registrar, so flush() saw no handle and threw the
   queue away. Every cart_smallprints beacon died that way (0 rows since 16
   Aug), plus anything queued in the last batching window of step 2.
   Module scope is the correct lifetime: this file outlives every step switch,
   and every screen that can fire a beacon already imports it. */
setUpsellProjectProvider(() => useStore.getState().project);

/* ============================================================
   Derived selectors (pure helpers over state)
   ============================================================ */

/** Prints mapped to the pricing engine's PrintSelection shape. */
export function toPrintSelections(prints: UiPrint[]): PrintSelection[] {
  return prints.map((p) => ({
    sizeId: p.sizeId,
    paper: p.paper,
    qty: p.qty,
    ...(p.frameId ? { frameId: p.frameId } : {}),
  }));
}

/**
 * Live estimated totals, or null when there is nothing priced yet.
 * BLOCKED prints (low-res, not overridden) are excluded until resolved via
 * swap / remove / "Print anyway" — see lib/sizePaper.pricedPrints.
 */
export function selectTotals(state: StoreState): Totals | null {
  if (!state.pricing) return null;
  const priced = pricedPrints(state.prints);
  if (priced.length === 0) return null;
  return computeTotals(state.pricing, toPrintSelections(priced));
}

/** Reactive live totals hook (memoized on prints + pricing). Excludes blocked prints. */
export function useTotals(): Totals | null {
  const prints = useStore((s) => s.prints);
  const pricing = useStore((s) => s.pricing);
  return useMemo(() => {
    if (!pricing) return null;
    const priced = pricedPrints(prints);
    return priced.length > 0 ? computeTotals(pricing, toPrintSelections(priced)) : null;
  }, [prints, pricing]);
}

/**
 * The live "needs attention" set: flagged prints (unresolved auto-crop or
 * blocked low-res) among the CURRENTLY SELECTED photos. Derived from prints +
 * selection so it stays in sync automatically — deselecting or removing a
 * flagged print drops it, re-selecting brings it back (the iteration-1 bug was
 * a stale snapshot that didn't do this).
 */
export function selectFlagged(state: StoreState): Set<string> {
  const out = new Set<string>();
  for (const p of state.prints) {
    if (isFlaggedPrint(p) && state.selection.has(p.photoId)) out.add(p.id);
  }
  return out;
}

/** Reactive count of flagged prints among the selection. */
export function useFlaggedCount(): number {
  const prints = useStore((s) => s.prints);
  const selection = useStore((s) => s.selection);
  return useMemo(
    () => prints.reduce((n, p) => (isFlaggedPrint(p) && selection.has(p.photoId) ? n + 1 : n), 0),
    [prints, selection],
  );
}

export function selectReadyCount(state: StoreState): number {
  return state.photos.filter((p) => p.status === 'ready').length;
}

export function selectHasReadyPhoto(state: StoreState): boolean {
  return state.photos.some((p) => p.status === 'ready');
}

/** Size lookup for the low-res reference size, used by the upload low-res pill. */
export function selectCatalog(state: StoreState) {
  return buildCatalog(state.pricing ?? seedPricing);
}
